<script setup>
/**
 * promo 优惠
 * service fee 服务费
 * tax exclusive 不含税，税价格
 * rounding 四舍五入
 * Total 总价
 * tax inclusive 含税提示
 */
import { computed, inject, defineProps } from 'vue';
import { GROUP_TYPE } from 'src/utils/config';
import MTooltip from 'src/components/MTooltip.vue';
import { useRoute } from 'src/utils/v2_helper/router';
import { useIns } from 'src/utils/v2_helper/common';
import { formatDate } from 'src/utils/base';
import ItemCard from '../../checkout/components/ItemCard.vue';
import PkgProductItem from '../../checkout/components/PkgProductItem.vue';
import { data } from './test';
import { transformProductData, isPkg, PRODUCT_STATUS } from '../util';

const app = useIns();
const route = useRoute();

const cartData = inject('prod_data', {});

const isFullCheckout = computed(() => route.name === 'FullCheckout');

const oData = computed(() => transformProductData(cartData.value));
// 商品列表
const groups = computed(() => {
  const gs = oData.value?.groups || [];
  return gs.sort((a, b) => b.statusDateTime - a.statusDateTime);
});
// 优惠券
const promo = computed(() => oData.value.promos[0] || null);
// 服务费
const serviceFee = computed(() => {
  const { serviceCharge } = oData.value;
  const { rate, amount } = serviceCharge;
  if (!amount && !rate) return null;
  return { amount, rate };
});
// 税
const taxFee = computed(() => {
  const { tax } = oData.value || {};
  const { rate, amount, inclusive } = tax || {};
  if (!rate && !amount) return null;
  return { rate, amount, inclusive };
});
// 总价
const total = computed(() => oData.value?.total);
// 划线价
const delTotal = computed(() => oData.value?.delTotal);

const hideRounding = inject('hide_rounding', false);
const hideTimeline = inject('hide_timeline', false);
</script>
<template>
  <section class="product">
    <div class="product-tile">{{ $t('fullDinein.product') }}</div>
    <div v-for="(group, i) in groups" :key="i" class="product-group">
      <div v-if="group.cartGroupId && !hideTimeline" class="group-tile">
        <span>#Order{{ groups.length - i }}</span>
        <div class="line" />
        <span>{{ formatDate(group.statusDateTime || 0, 'HH:mm') }}</span>
      </div>
      <ItemCard
        v-for="prod in group.cartItems"
        :key="prod.id"
        :product="prod"
        :class="{
          disable: prod.groupStatusId === PRODUCT_STATUS.ITEM_GROUP_CANCELED,
        }"
      >
        <template #pkg>
          <PkgProductItem :product="prod" />
        </template>
      </ItemCard>
    </div>
    <div class="product-price">
      <!--Promo-->
      <div v-if="promo" class="product-price-item promo">
        <span>{{ promo.name }}</span>
        <span class="price">{{ $n(cartData.promoTotalWithoutGift || 0, 'currency', 'id-ID') }}</span>
      </div>
      <!--ServiceFee-->
      <div v-if="!!serviceFee" class="product-price-item">
        <span>
          {{ $t('checkout.serviceCharge') }}
          <span v-if="serviceFee.rate">{{ serviceFee.rate }}%</span>
        </span>
        <MTooltip :text="$t('checkout.serviceChargeTip')">
          <q-icon name="app:tipsIcon" tip size="16px" />
        </MTooltip>
        <span class="price">{{ $pid(serviceFee.amount) }}</span>
      </div>
      <!--TaxFee-->
      <div v-if="!!taxFee && !taxFee.inclusive" class="product-price-item">
        <span>
          {{ $t('checkout.taxExclusive') }}
          <span v-if="taxFee.rate">{{ taxFee.rate }}%</span>
        </span>
        <MTooltip :text="$t('checkout.taxExclusiveTip')">
          <q-icon name="app:tipsIcon" tip size="16px" />
        </MTooltip>
        <span class="price">{{ $pid(taxFee.amount) }}</span>
      </div>
      <!--Rounding-->
      <div v-if="oData.rounding && !hideRounding" class="product-price-item">
        <span>{{ $t('checkout.rounding') }}</span>
        <span class="price">{{ $pid(oData.rounding) }}</span>
      </div>
      <!--Total-->
      <div class="product-price-item">
        <span>{{ $t('result.total') }}</span>
        <span class="price total" bold>
          <span class="normal">{{ $pid(total || 0) }}</span>
          <span v-if="total !== delTotal" class="del">{{ $pid(delTotal || 0) }}</span>
        </span>
      </div>
      <!--Tax Inclusive-->
      <div v-if="!!taxFee && taxFee.inclusive" class="product-price-tip">
        {{ $t('checkout.taxInclusiv') }} {{ taxFee.rate }}
        {{ $pid(taxFee.amount) }}
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
*[red] {
  color: red;
}
.m-tile {
  color: Black;
  font-weight: bold;
  font-size: 12px;
}
*[tip] {
  margin-left: 4px;
}
.product {
  padding: 16px;
  &-tile {
    @extend .m-tile;
  }
  &-price {
    margin-top: 8px;
    color: #6b7280;
    &-item {
      display: flex;
      font-size: 12px;
      & + & {
        margin-top: 6px;
      }
      .price {
        margin-left: auto;
        color: $black;
        display: flex;
        flex-direction: column;
      }
      .price.total {
        font-size: 16px;
      }
      .del {
        font-size: $font-size-1;
        color: $grayFont;
        text-decoration: line-through;
        align-self: flex-end;
        font-weight: normal;
        line-height: 0.8;
      }
    }
    &-item.promo {
      color: $primary;
      .price {
        color: $primary;
      }
    }
    &-tip {
      text-align: right;
      font-size: 12px;
    }
  }
  &-group {
    font-size: 12px;
    .group-tile {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 0;
      color: #9ca3af;
    }
    .line {
      margin: 0 8px;
      border-top: solid 1px #e5e7eb;
      flex: 1;
    }
  }
  .disable {
    opacity: 0.5;
  }
}
</style>
